import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { CustomButton, Text } from '../../../shared/uiComponents';
import { months } from '../Helpers/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	setDay,
	setEndOfWeek,
	setLastDayOfMonth,
	setLastDayOfPreviousMonth,
	setMonth,
	setSelectedDay,
	setStartOfWeek,
	setWeekDay,
	setYear,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { useScreenWidth } from '../../../shared/Helpers/hooks';

const Navigation = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const shouldMonthNameSlice = screenWidth < 1540;

	const view = useSelector((state) => state.calendarHelper.calendarView);
	const day = useSelector((state) => state.calendarHelper.day);
	const month = useSelector((state) => state.calendarHelper.month);
	const year = useSelector((state) => state.calendarHelper.year);

	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const startOfWeek = useSelector((state) => state.calendarHelper.weekView.startOfWeek);
	const endOfWeek = useSelector((state) => state.calendarHelper.weekView.endOfWeek);

	const today = new Date();
	const currentDay = today.getDate();
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();

	const weekRange = days.slice(startOfWeek, endOfWeek);
	const startDay = weekRange[0]?.day;
	const endDay = weekRange.at(-1)?.day;

	useEffect(() => {
		if (view !== 1) return;

		if (month === currentMonth) {
			const currentStart = Math.floor(days.findIndex((x) => x.day === currentDay && x.status === 1) / 7);

			dispatch(setStartOfWeek(currentStart * 7));
			dispatch(setEndOfWeek(currentStart * 7 + 7));

			return;
		}

		if (month < currentMonth) {
			const currentStart = Math.floor(
				// @ts-expect-error Investigate - TODO does days JS has findLastIndex method?
				days.findLastIndex((x) => x.status === 1) / 7,
			);
			dispatch(setStartOfWeek(currentStart * 7));
			dispatch(setEndOfWeek(days.length));

			return;
		}
		dispatch(setStartOfWeek(0));
		dispatch(setEndOfWeek(7));
	}, [view, days, currentDay, month, currentMonth, dispatch]);

	const actionHandler = (selectedMonth: number, selectedYear: number) => {
		dispatch(setMonth(selectedMonth));
		dispatch(setYear(selectedYear));

		const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
		dispatch(setLastDayOfMonth(lastDayOfMonth));

		const lastDayOfLastMonth = new Date(selectedYear, selectedMonth, 0).getDate();
		dispatch(setLastDayOfPreviousMonth(lastDayOfLastMonth));

		const nextWeekDay = new Date(selectedYear, selectedMonth, 1).getDay();
		dispatch(setWeekDay(nextWeekDay));

		const prevDay = day - 1;

		if (view === 2 && !prevDay) {
			dispatch(setDay(lastDayOfMonth));
			dispatch(setSelectedDay({ day: prevDay, month: selectedMonth, year: selectedYear, status: 1 }));
		}
	};

	const nextHandler = () => {
		if (view === 1 && endOfWeek !== 35) {
			dispatch(setStartOfWeek(startOfWeek + 7));
			dispatch(setEndOfWeek(endOfWeek + 7));

			return;
		}

		if (view === 2) {
			const nextDay = day + 1;
			const isCurrentMonthDaysCount = days.filter((x) => x.status === 1).length;

			if (nextDay <= isCurrentMonthDaysCount) {
				dispatch(setDay(nextDay));
				dispatch(setSelectedDay({ day: nextDay, month, year, status: 1 }));

				return;
			}
			dispatch(setDay(1));
			dispatch(setSelectedDay({ day: 1, month, year, status: 1 }));
		}

		if (month === 11) {
			const nextYear = year + 1;

			actionHandler(0, nextYear);

			return;
		}

		const nextMonth = month + 1;
		actionHandler(nextMonth, year);
	};

	const prevHandler = () => {
		if (view === 1 && Boolean(startOfWeek)) {
			dispatch(setStartOfWeek(startOfWeek - 7));
			dispatch(setEndOfWeek(endOfWeek - 7));

			return;
		}

		if (view === 2) {
			const prevDay = day - 1;

			if (prevDay) {
				dispatch(setDay(prevDay));
				dispatch(setSelectedDay({ day: prevDay, month, year, status: 1 }));

				return;
			}
		}

		if (month === 0) {
			const previousYear = year - 1;

			actionHandler(11, previousYear);

			return;
		}

		const prevMonth = month - 1;
		actionHandler(prevMonth, year);
	};

	const todayHandler = () => {
		actionHandler(currentMonth, currentYear);
		dispatch(
			setSelectedDay({
				day: currentDay,
				month: currentMonth,
				year: currentYear,
				status: 1,
			}),
		);
	};

	const monthName = shouldMonthNameSlice ? months[month].slice(0, 3) : months[month];

	const headerTitle =
		view === 0
			? `${monthName} ${year}`
			: view === 1
				? `${monthName} ${startDay}-${endDay}, ${year}`
				: view === 2
					? `${monthName} ${day}, ${year}`
					: '';

	return (
		<>
			<div className="navigation">
				<CustomButton
					iconOnly={<FontAwesomeIcon icon={faChevronLeft} />}
					onClick={prevHandler}
					buttonType="secondaryGray"
					style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
					aria-label="Previous"
				/>
				<CustomButton title="Today" buttonType="secondaryGray" onClick={todayHandler} style={{ borderRadius: 0 }} />
				<CustomButton
					iconOnly={<FontAwesomeIcon icon={faChevronRight} />}
					onClick={nextHandler}
					buttonType="secondaryGray"
					style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
					aria-label="Next"
				/>
			</div>
			<Text title={headerTitle} type="h5" font="header" fontWeight="semiBold" className="currentDate" />
		</>
	);
};

export default Navigation;
